import PropTypes from 'prop-types';
import { useEffect, forwardRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { m } from 'framer-motion';
import { NAVBAR } from '../../config';
import { styled, useTheme } from '@mui/material/styles';
import { Box, Drawer, Stack, IconButton, Typography } from '@mui/material';
import useResponsive from '../../hooks/useResponsive';
import Logo from '../../components/Logo';
import Scrollbar from '../../components/Scrollbar';
import DrawerMenuListView from '../../components/DrawerMenuListView';
import { useAppSelector, useAppDispatch } from '../../store';
import { logoutUser } from '../../store/auth/api';
import useCollapseDrawer from '../../hooks/useCollapseDrawer';
import cssStyles from '../../utils/cssStyles';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import HomeIcon from '@mui/icons-material/Home';
import { FaPeopleGroup } from 'react-icons/fa6';
import { FaBuilding } from 'react-icons/fa';
import { FaLocationDot } from 'react-icons/fa6';
import { FaRegBuilding } from 'react-icons/fa';
import { RiAdvertisementFill } from 'react-icons/ri';
import { RiAdvertisementLine } from 'react-icons/ri';
import { IoSettingsSharp } from 'react-icons/io5';
import { MdConnectWithoutContact } from 'react-icons/md';
import { IoIosNotifications } from 'react-icons/io';
import { RiMoneyDollarCircleFill } from 'react-icons/ri';
import { FaChartLine } from 'react-icons/fa';
import { FaChartBar } from 'react-icons/fa';
import { config } from '../../utils/utils';

const DRAWER_WIDTH = 280;

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    width: DRAWER_WIDTH,
  },
}));

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

const IconButtonAnimate = forwardRef(
  ({ children, size = 'medium', ...other }: any, ref) => (
    <AnimateWrap size={size}>
      <IconButton
        size={size}
        ref={ref}
        {...other}
      >
        {children}
      </IconButton>
    </AnimateWrap>
  )
);

IconButtonAnimate.propTypes = {
  children: PropTypes.node.isRequired,
  color: PropTypes.oneOf([
    'inherit',
    'default',
    'primary',
    'secondary',
    'info',
    'success',
    'warning',
    'error',
  ]),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
};
const varSmall = {
  hover: { scale: 1.1 },
  tap: { scale: 0.95 },
};

const varMedium = {
  hover: { scale: 1.09 },
  tap: { scale: 0.97 },
};

const varLarge = {
  hover: { scale: 1.08 },
  tap: { scale: 0.99 },
};

AnimateWrap.propTypes = {
  children: PropTypes.node.isRequired,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
};

function AnimateWrap({ size, children }: { size: any; children: any }) {
  const isSmall = size === 'small';
  const isLarge = size === 'large';

  return (
    <Box
      component={m.div}
      whileTap='tap'
      whileHover='hover'
      variants={(isSmall && varSmall) || (isLarge && varLarge) || varMedium}
      sx={{
        display: 'inline-flex',
      }}
    >
      {children}
    </Box>
  );
}

function CollapseButton({
  onToggleCollapse,
  collapseClick,
}: {
  onToggleCollapse?: any;
  collapseClick?: any;
}) {
  return (
    <IconButtonAnimate onClick={onToggleCollapse}>
      <Box
        sx={{
          lineHeight: 0,
          transition: (theme) =>
            theme.transitions.create('transform', {
              duration: theme.transitions.duration.shorter,
            }),
          ...(collapseClick && {
            transform: 'rotate(180deg)',
          }),
        }}
      >
        <KeyboardDoubleArrowLeftIcon />
      </Box>
    </IconButtonAnimate>
  );
}

export default function DashboardSidebar({
  isOpenSidebar,
  onCloseSidebar,
}: {
  isOpenSidebar: boolean;
  onCloseSidebar: () => void;
}) {
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { userRole } = useAppSelector((state) => state.auth.user);
  const isDesktop = useResponsive('up', 'lg', 'as', 'as');
  // const { totalRecords, status } = useAppSelector(
  //   (state) => state.notification
  // );

  const {
    isCollapse,
    collapseClick,
    collapseHover,
    onToggleCollapse,
    onHoverEnter,
    onHoverLeave,
  }: any = useCollapseDrawer();
  const [sidebarItemState, setSidebarItemState] = useState<any>();

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
  }, [pathname, isDesktop]);

  useEffect(() => {
    if (!userRole) {
      localStorage.clear();
      dispatch(logoutUser());
    }
  }, [dispatch, userRole]);

  useEffect(() => {
    (async () => {
      const allItems: any = await handleRenderSidebarConfig();
      setSidebarItemState(allItems);
    })();
  }, []);

  // useEffect(() => {
  //   if (status === 'idle') {
  //     dispatch(
  //       findNotification({
  //         isRead: false,
  //         isExport: true,
  //         moduleType: NotificationModuleType.order,
  //         receiverUserId: id,
  //       })
  //     );
  //   }
  // }, [dispatch, status]);

  const handleRenderSidebarConfig = () => {
    if (userRole === 'admin') {
      return [
        {
          items: [
            {
              title: 'Dashboard',
              path: '/',
              icon: <HomeIcon fontSize='small' />,
            },
            {
              title: 'KPI Dashboard',
              path: '/kpi/advertiser-performance',
              icon: <FaChartLine fontSize='small' />,
            },
            {
              title: 'Wishlist Analyzer',
              path: '/wishlist-analyzer',
              icon: <FaChartBar fontSize='small' />,
            },
            {
              title: 'users',
              path: '/users',
              icon: <FaPeopleGroup fontSize='small' />,
            },
          ],
        },
        {
          items: [
            {
              title: 'Property',
              icon: <FaBuilding fontSize='small' />,
              children: [
                {
                  title: 'property-category',
                  path: '/property-category',
                  icon: <FaBuilding fontSize='small' />,
                },
                {
                  title: 'property-sub-category',
                  path: '/property-sub-category',
                  icon: <FaRegBuilding fontSize='small' />,
                },
                {
                  title: 'property-feature',
                  path: '/property-feature',
                  icon: <FaRegBuilding fontSize='small' />,
                },
                {
                  title: 'property-facility',
                  path: '/property-facility',
                  icon: <FaRegBuilding fontSize='small' />,
                },
              ],
            },
          ],
        },
        {
          items: [
            {
              title: 'location-management',
              path: '/location-management',
              icon: <FaLocationDot fontSize='small' />,
            },
            {
              title: 'advertise-package',
              path: '/advertise-package',
              icon: <RiAdvertisementFill fontSize='small' />,
            },
            {
              title: 'advertise',
              path: '/advertise',
              icon: <RiAdvertisementLine fontSize='small' />,
            },
            {
              title: 'Payment',
              path: '/payment',
              icon: <RiMoneyDollarCircleFill fontSize='small' />,
            },
            {
              title: 'History',
              icon: <RiMoneyDollarCircleFill fontSize='small' />,
              children: [
                {
                  title: 'Purchase History',
                  path: '/credit-history/purchase',
                },
                {
                  title: 'Allocation History',
                  path: '/credit-history/allocation',
                },
                {
                  title: 'Credit Usage History',
                  path: '/credit-history/usage',
                },
                {
                  title: 'Expiration History',
                  path: '/credit-history/expiration',
                },
                {
                  title: 'Account Type Conversion',
                  path: '/history/account-type-conversion',
                },
              ],
            },
          ],
        },
        {
          items: [
            {
              title: 'General Settings',
              icon: <IoSettingsSharp fontSize='small' />,
              children: [
                {
                  title: 'Settings',
                  path: '/settings',
                },
                {
                  title: 'Rejection Reasons',
                  path: '/registration-rejection-reasons',
                },
              ],
            },
          ],
        },
        {
          items: [
            {
              title: 'Notification',
              path: '/notification',
              icon: <IoIosNotifications fontSize='small' />,
            },
          ],
        },
        {
          items: [
            {
              title: 'Support',
              icon: <MdConnectWithoutContact fontSize='small' />,
              children: [
                {
                  title: 'Contact Form',
                  path: '/contact-form',
                },
              ],
            },
          ],
        },
      ];
    }
    if (userRole === 'adminTier2') {
      return [
        {
          items: [
            {
              title: 'Dashboard',
              path: '/',
              icon: <HomeIcon fontSize='small' />,
            },
            {
              title: 'users',
              path: '/users',
              icon: <FaPeopleGroup fontSize='small' />,
            },
            {
              title: 'advertise',
              path: '/advertise',
              icon: <RiAdvertisementLine fontSize='small' />,
            },
            {
  title: 'Support',
  icon: <MdConnectWithoutContact fontSize='small' />,
  children: [
    {
      title: 'Contact Form',
      path: '/contact-form',
    },
  ],
},
          ],
        },
      ];
    }
    if (userRole === 'adminTier3') {
      return [
        {
          items: [
            {
              title: 'Dashboard',
              path: '/',
              icon: <HomeIcon fontSize='small' />,
            },
            {
              title: 'advertise',
              path: '/advertise',
              icon: <RiAdvertisementLine fontSize='small' />,
            },
          ],
        },
      ];
    }
    if (userRole === 'company') {
      return [
        {
          items: [
            {
              title: 'Dashboard',
              path: '/company-dashboard',
              icon: <HomeIcon fontSize='small' />,
            },
            {
              title: 'KPI Dashboard',
              path: '/company-ads-performance',
              icon: <FaChartLine fontSize='small' />,
            },
            {
              title: 'Credit Management',
              path: '/credit-management',
              icon: <RiMoneyDollarCircleFill fontSize='small' />,
            },
            {
              title: 'Purchase Credits',
              path: '/purchase-credits',
              icon: <RiMoneyDollarCircleFill fontSize='small' />,
            },
            {
              title: 'History',
              icon: <RiMoneyDollarCircleFill fontSize='small' />,
              children: [
                {
                  title: 'Purchase History',
                  path: '/credit-history/purchase',
                },
                {
                  title: 'Allocation History',
                  path: '/credit-history/allocation',
                },
                {
                  title: 'Credit Usage History',
                  path: '/credit-history/usage',
                },
                {
                  title: 'Expiration History',
                  path: '/credit-history/expiration',
                },
              ],
            },
            {
              title: 'Agent Management',
              path: '/agent-management',
              icon: <FaPeopleGroup fontSize='small' />,
            },
            {
              title: 'Agent Transfer Logs',
              path: '/agent-transfer-logs',
              icon: <FaPeopleGroup fontSize='small' />,
            },
            {
              title: 'Ads',
              path: '/company-advertise',
              icon: <RiAdvertisementLine fontSize='small' />,
            },
            {
              title: 'License Renewal',
              path: '/license-renewal',
              icon: <IoSettingsSharp fontSize='small' />,
            },
          ],
        },
        {
          items: [
            {
              title: 'Support',
              icon: <MdConnectWithoutContact fontSize='small' />,
              children: [
                {
                  title: 'User Guide (EN)',
                  path:
                    process.env.REACT_APP_COMPANY_USER_GUIDE_1_URL ||
                    '/user-guides/AQARI_Company_Dashboard_Guide_EN_v2.pdf',
                },
                {
                  title: 'User Guide (AR)',
                  path:
                    process.env.REACT_APP_COMPANY_USER_GUIDE_2_URL ||
                    '/user-guides/AQARI_Company_Dashboard_Guide_AR_v2.pdf',
                },
              ],
            },
          ],
        },
      ];
    }
    return [];
  };

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Stack
        spacing={3}
        sx={{
          pt: 3,
          pb: 2,
          px: 2.5,
          flexShrink: 0,
          ...(isCollapse && { alignItems: 'center' }),
        }}
      >
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <Logo isCollapse={isCollapse} />

          {isDesktop && !isCollapse && (
            <CollapseButton
              onToggleCollapse={onToggleCollapse}
              collapseClick={collapseClick}
            />
          )}
        </Stack>
      </Stack>

      <DrawerMenuListView
        menuConfig={sidebarItemState}
        isCollapse={isCollapse}
      />

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ px: 2.5, pb: 3, mt: 10 }}>
        <Stack
          alignItems='center'
          spacing={3}
          sx={{ pt: 5, borderRadius: 2, position: 'relative' }}
        >
          <Typography variant='caption'>Version: {config.VERSION}</Typography>
        </Stack>
      </Box>
    </Scrollbar>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse
            ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH
            : NAVBAR.DASHBOARD_WIDTH,
        },
        ...(collapseClick && {
          position: 'absolute',
        }),
      }}
    >
      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: {
              width: NAVBAR.DASHBOARD_WIDTH,
              bgcolor: 'background.panelBackground',
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant='persistent'
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: NAVBAR.DASHBOARD_WIDTH,
              borderRightStyle: 'dashed',
              bgcolor: 'background.panelBackground',
              transition: (theme: any) =>
                theme.transitions.create('width', {
                  duration: theme.transitions.duration.standard,
                }),
              ...(isCollapse && {
                width: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
              }),
              ...(collapseHover && {
                ...cssStyles(theme).bgBlur({}),
                boxShadow: (theme: any) => theme.customShadows.z24,
                backgroundColor: theme.palette.background,
              }),
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  );
}
